import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { identifyUser, resetUser } from "@/lib/analytics";
import { trackRegistration } from "@/lib/metaPixel";
import { readTokenHashLogin } from "@/lib/tokenHashLogin";
import { translationsForPath } from "@/i18n/translations";

/**
 * This provider wraps the router and LocaleProvider, so it cannot use
 * useLocale(): read the language off the URL when the toast is shown.
 */
function authStrings() {
  const path = typeof window === "undefined" ? "/" : window.location.pathname;
  return translationsForPath(path);
}

const AUTH_INIT_TIMEOUT_MS = 15_000;
// Matches track-signup's server-side limit on what counts as a new account.
const NEW_ACCOUNT_WINDOW_MS = 60 * 60 * 1000;

interface SecureAuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const SecureAuthContext = createContext<SecureAuthContextType | undefined>(undefined);

// SSR-safe default values
const ssrDefaultValue: SecureAuthContextType = {
  user: null,
  session: null,
  loading: false,
  signOut: async () => {},
};

export const useSecureAuth = () => {
  const context = useContext(SecureAuthContext);
  // Return SSR-safe defaults when context is undefined (SSR)
  if (context === undefined) {
    // Check if we're on the server
    if (typeof window === 'undefined') {
      return ssrDefaultValue;
    }
    throw new Error("useSecureAuth must be used within a SecureAuthProvider");
  }
  return context;
};

interface SecureAuthProviderProps {
  children: ReactNode;
}

export const SecureAuthProvider = ({ children }: SecureAuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Email login links carry a token hash for this page to verify (see
    // magicLoginLink); until that is done the empty initial session is not the
    // answer, and reporting it would flash the login prompt.
    const pendingTokenHash = readTokenHashLogin(new URL(window.location.href));

    let resolved = false;
    const finishLoading = () => {
      if (!resolved) {
        resolved = true;
        setLoading(false);
      }
    };

    const timeoutId = window.setTimeout(() => {
      if (!resolved) {
        console.warn('Auth initialization timed out');
        finishLoading();
      }
    }, AUTH_INIT_TIMEOUT_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);

        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !pendingTokenHash)) {
          finishLoading();
        }

        // Tie analytics (incl. the conversion funnel) to an identified person so
        // anonymous pre-login events stitch onto the user after they sign in.
        const userToIdentify = nextSession?.user;
        if (userToIdentify && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
          // Id only — sending the email on to GA4 would breach Google's no-PII rule.
          identifyUser(userToIdentify.id);

          // First sign-in of a brand-new account — how Google/Facebook sign-ups
          // (which never pass through the sign-up form) reach Meta as a
          // registration. trackRegistration dedupes per account. Accounts made
          // by lead-intake are skipped: Meta already has those as native leads.
          const createdAt = Date.parse(userToIdentify.created_at);
          if (
            Date.now() - createdAt < NEW_ACCOUNT_WINDOW_MS &&
            userToIdentify.user_metadata?.signup_source !== 'meta_lead_form'
          ) {
            // Deferred: it calls an edge function, and a Supabase call made
            // inside this callback waits on the auth lock the callback holds.
            const provider = String(userToIdentify.app_metadata?.provider ?? 'email');
            window.setTimeout(() => trackRegistration(userToIdentify.id, provider), 0);
          }
        }

        if (event === 'SIGNED_OUT') {
          resetUser();
          const t = authStrings();
          toast({
            title: t.auth.signedOutTitle,
            description: t.auth.signedOutDesc,
          });
        }
      }
    );

    const initializeAuth = async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');

        if (pendingTokenHash) {
          const { error } = await supabase.auth.verifyOtp(pendingTokenHash);
          if (error) {
            // Expired or already used: the page carries on logged out and the
            // auth gate offers the normal login.
            console.error('Email link login failed:', error);
          }
          url.searchParams.delete('token_hash');
          url.searchParams.delete('type');
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('OAuth code exchange failed:', error);
          } else {
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            const nextUrl = `${url.pathname}${url.search}${url.hash}`;
            window.history.replaceState({}, '', nextUrl);
          }
        }

        const { data: { session: existingSession }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Failed to restore session:', error);
        }

        setSession(existingSession);
        setUser(existingSession?.user ?? null);
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        finishLoading();
      }
    };

    void initializeAuth();

    return () => {
      window.clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [toast]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      const t = authStrings();
      toast({
        title: t.common.error,
        description: t.auth.signOutError,
        variant: "destructive",
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
  };

  return (
    <SecureAuthContext.Provider value={value}>
      {children}
    </SecureAuthContext.Provider>
  );
};